/**
 * Lightweight engagement signals (local only).
 * First-run guide, last-seen for comeback, activation progress.
 */

import { t } from "@/i18n";

const KEYS = {
  firstRunDismissed: "andrum.engagement.first_run_dismissed",
  firstCheckInDone: "andrum.engagement.first_checkin",
  firstExperimentStarted: "andrum.engagement.first_experiment",
  lastSeenAt: "andrum.engagement.last_seen",
  comebackDismissedDay: "andrum.engagement.comeback_dismissed_day",
} as const;

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

export function touchLastSeen(): void {
  try {
    localStorage.setItem(KEYS.lastSeenAt, new Date().toISOString());
  } catch {
    // ignore
  }
}

export function getLastSeenAt(): Date | null {
  try {
    const raw = localStorage.getItem(KEYS.lastSeenAt);
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

/** Whole calendar days since last open (0 = today / first open). */
export function daysSinceLastSeen(): number {
  const last = getLastSeenAt();
  if (!last) return 0;
  const now = new Date();
  const a = Date.UTC(last.getFullYear(), last.getMonth(), last.getDate());
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

export function isFirstRunGuideVisible(): boolean {
  try {
    if (localStorage.getItem(KEYS.firstRunDismissed) === "1") return false;
    // Hide once both core actions done
    if (
      localStorage.getItem(KEYS.firstCheckInDone) === "1" &&
      localStorage.getItem(KEYS.firstExperimentStarted) === "1"
    ) {
      return false;
    }
    return true;
  } catch {
    return true;
  }
}

export function dismissFirstRunGuide(): void {
  try {
    localStorage.setItem(KEYS.firstRunDismissed, "1");
  } catch {
    // ignore
  }
}

export function markFirstCheckInDone(): void {
  try {
    localStorage.setItem(KEYS.firstCheckInDone, "1");
  } catch {
    // ignore
  }
}

export function markFirstExperimentStarted(): void {
  try {
    localStorage.setItem(KEYS.firstExperimentStarted, "1");
  } catch {
    // ignore
  }
}

export function firstRunProgress(): {
  checkIn: boolean;
  experiment: boolean;
} {
  try {
    return {
      checkIn: localStorage.getItem(KEYS.firstCheckInDone) === "1",
      experiment: localStorage.getItem(KEYS.firstExperimentStarted) === "1",
    };
  } catch {
    return { checkIn: false, experiment: false };
  }
}

/**
 * Soft comeback after gap. Threshold: 3+ days since last open.
 * Dismissed for the current calendar day only.
 */
export function shouldShowComeback(minDays = 3): boolean {
  try {
    const gap = daysSinceLastSeen();
    // On first ever open lastSeen is null → gap 0; after touchLastSeen we need
    // to read gap *before* touch. Callers should check before touchLastSeen.
    if (gap < minDays) return false;
    if (localStorage.getItem(KEYS.comebackDismissedDay) === todayKey()) {
      return false;
    }
    return true;
  } catch {
    return false;
  }
}

export function dismissComebackToday(): void {
  try {
    localStorage.setItem(KEYS.comebackDismissedDay, todayKey());
  } catch {
    // ignore
  }
}

export function comebackCopy(days: number): {
  title: string;
  body: string;
} {
  if (days >= 14) {
    return {
      title: t("today.comeback.longTitle"),
      body: t("today.comeback.longBody"),
    };
  }
  if (days >= 7) {
    return {
      title: t("today.comeback.weekTitle"),
      body: t("today.comeback.weekBody"),
    };
  }
  return {
    title: t("today.comeback.shortTitle"),
    body: t("today.comeback.shortBody"),
  };
}
